import { useMemo, useRef, useEffect, useState } from 'react';
import { parseMessageContent } from '../../../../shared/utils/messageParser';
import { BibleVersesCarousel } from '../../../bible/components/verses/BibleVersesCarousel';
import { useErrorHandler } from '../../../../shared/hooks/useErrorHandler';
import { ErrorBoundary } from '../../../../shared/components/error/ErrorBoundary';
import { ErrorMessage } from '../../../../shared/components/error/ErrorMessage';
import { Logo } from '../../../../shared/components/layout/Logo';


interface AssistantMessageProps {
  content: string;
  isStreaming?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
  onTellMeMore: (verse: { book: string; chapter: string; verse: string }) => void;
}

export function AssistantMessage({ 
  content, 
  isStreaming = false,
  hasError = false,
  onRetry,
  onTellMeMore 
}: AssistantMessageProps) {
  const { handleAsyncError } = useErrorHandler();
  const prevContentRef = useRef('');
  const [showVerses, setShowVerses] = useState(true);
  const [parseError, setParseError] = useState<Error | null>(null);
  
  const parsedContent = useMemo(() => {
    try {
      if (!content) {
        return { message: '', verses: {} };
      }

      if (isStreaming) {
        try {
          return parseMessageContent(content);
        } catch (error) {
          return { message: '', verses: {} };
        }
      }

      const parsed = parseMessageContent(content);
      return {
        message: parsed.message || '',
        verses: parsed.verses || {}
      };
    } catch (error) {
      if (!isStreaming) {
        handleAsyncError(Promise.reject(error), {
          severity: 'medium',
          userMessage: 'Unable to parse message content',
          shouldReport: true,
          context: { content }
        });
        setParseError(error as Error);
      }
      return { message: content, verses: {} };
    }
  }, [content, isStreaming]);

  useEffect(() => {
    try {
      const prevParsed = parseMessageContent(prevContentRef.current);
      const versesChanged = JSON.stringify(prevParsed.verses) !== JSON.stringify(parsedContent.verses);
      
      if (versesChanged && !isStreaming) {
        setShowVerses(true);
      }
      
      prevContentRef.current = content;
    } catch (error) {
      handleAsyncError(Promise.reject(error), {
        severity: 'low',
        userMessage: 'Unable to process message updates',
        shouldReport: false
      });
    }
  }, [content, isStreaming, parsedContent.verses]);

  if (hasError || parseError) {
    return (
      <ErrorMessage
        message={parseError ? 'Unable to display message properly' : 'Failed to load response'}
        action={!parseError ? onRetry : undefined}
        actionLabel="Retry"
        variant="card"
      />
    );
  }

  return (
    <ErrorBoundary
      fallback={({ error }) => (
        <ErrorMessage
          message="Unable to display message"
          variant="card"
        />
      )}
    >
      <div className="">
        <div className="flex items-center justify-left gap-3 mb-6 text-[#361E06]">
          <Logo size={24} />
          <div className="font-medium text-lg">Bible based answer</div>
        </div>
        <div className="sm:px-0 max-w-[100vw]">

          <div className="">
            <div className="prose max-w-none">
              <div className="text-[#361E06] font-newsreader text-[22px] leading-[1.45]">
              {parsedContent.message.split('\n\n').map((paragraph, index) => (
               <p key={index} className="mb-5 last:mb-0">
                  {paragraph}
              </p>
            ))} 
              </div>
            </div>

            {Object.keys(parsedContent.verses).length > 0 && showVerses && (
              <div className="mt-8">
                <BibleVersesCarousel
                  verses={parsedContent.verses}
                  onTellMeMore={onTellMeMore}
                  isStreaming={isStreaming}
                />
              </div>
            )}
          </div>
        </div>

      </div>
    </ErrorBoundary>
  );
} 